import { IndeterminateCheckbox } from "@bcc/components/ui/checkbox";
import { cn } from "@bcc/lib/utils";
import type { DataTableRow, DataTableRowTone } from "@client-shared";
import { flexRender, type Row, type RowSelectionState } from "@tanstack/react-table";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import * as React from "react";
import { setExpanderButtonTestId, setRowCheckboxTestId, setRowTestId } from "./data-table.utils";

import type { GroupedRowActionHelpers } from "./data-table.types";

interface DataTableRowComponentProps<T extends object> {
  row: Row<DataTableRow<T>>;
  selectable?: boolean;
  onClick?: (row: Row<DataTableRow<T>>) => void;
  showExpanderColumn?: boolean;
  showGroupCount?: boolean;
  renderGroupSummary?: (rows: DataTableRow<T>[]) => React.ReactNode;
  rowSelection?: RowSelectionState;
  groupedRowActions?: (row: Row<DataTableRow<T>>, helpers: GroupedRowActionHelpers<T>) => React.ReactNode;
  helpers?: GroupedRowActionHelpers<T>;
}

/**
 * Returns true when every non-disabled leaf row in the group is selected.
 * Uses rowSelection directly because getIsAllSubRowsSelected() includes
 * disabled rows (which can never be selected), so it always returns false.
 */
function getIsGroupChecked<T extends object>(groupRow: Row<DataTableRow<T>>, rowSelection?: RowSelectionState): boolean {
  const selectableLeaves = groupRow.getLeafRows().filter((r) => !r.original.isDisabled);
  if (selectableLeaves.length === 0) return false;
  const selection = (rowSelection ?? {}) as Record<string, boolean>;
  return selectableLeaves.every((r) => selection[r.id] === true);
}

/**
 * Custom indeterminate check for grouped rows.
 * Excludes disabled rows from the calculation — matching client DataTable.
 */
function getIsGroupIndeterminate<T extends object>(groupRow: Row<DataTableRow<T>>, rowSelection?: RowSelectionState): boolean {
  if (!groupRow.getIsGrouped() || !groupRow.subRows || !rowSelection) {
    return groupRow.getIsSomeSelected();
  }
  const selection = rowSelection as Record<string, boolean>;
  let hasSelected = false;
  let hasUnselected = false;

  const checkGroup = (r: Row<DataTableRow<T>>): void => {
    if (r.getIsGrouped() && r.subRows) {
      for (const subRow of r.subRows) {
        checkGroup(subRow);
      }
      return;
    }
    if (r.original.isDisabled) return;
    if (selection[r.id] === true) {
      hasSelected = true;
    } else {
      hasUnselected = true;
    }
  };

  checkGroup(groupRow);
  return hasSelected && hasUnselected;
}

/**
 * Returns true if every selectable leaf row in the group is disabled.
 */
function isGroupAllDisabled<T extends object>(groupRow: Row<DataTableRow<T>>): boolean {
  return groupRow.getLeafRows().every((r) => r.original.isDisabled);
}

/**
 * Returns the Tailwind class for a row tone.
 * Uses explicit class strings so Tailwind can detect them at build time.
 */
function getToneClass(tone?: DataTableRowTone): string {
  switch (tone) {
    case "success":
      return "bg-green-500/20 dark:bg-green-500/20";
    case "warning":
      return "bg-amber-500/20 dark:bg-amber-500/20";
    case "critical":
      return "bg-red-500/20 dark:bg-red-500/20";
    case "subdued":
      return "bg-muted/50 text-muted-foreground";
    case "canceled":
      return "bg-muted/50 text-muted-foreground line-through";
    default:
      return "";
  }
}

/**
 * DataTableRowComponent
 *
 * Renders a single table row with optional selection checkbox,
 * row tones, click handling, and grouped row support.
 */
export function DataTableRowComponent<T extends object>({
  row,
  selectable = false,
  onClick,
  showExpanderColumn = false,
  showGroupCount = false,
  renderGroupSummary,
  rowSelection,
  groupedRowActions,
  helpers,
}: DataTableRowComponentProps<T>) {
  const original = row.original;
  const tone = original.tone;
  const isDisabled = original.isDisabled;
  const isLoading = original.isLoading;
  const hasError = original.hasError;
  const isGrouped = row.getIsGrouped();
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const depth = row.depth;
  const isGroupDisabled = isGrouped && isGroupAllDisabled(row);
  const handleClick = React.useCallback(() => {
    if (onClick && !isDisabled) {
      onClick(row);
    }
  }, [onClick, row, isDisabled]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && onClick && !isDisabled) {
        e.preventDefault();
        onClick(row);
      }
    },
    [onClick, row, isDisabled]
  );

  const handleExpandClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      row.toggleExpanded();
    },
    [row]
  );

  const handleCheckboxChange = React.useCallback(
    (checked: boolean) => {
      if (isGrouped) {
        row.getLeafRows().forEach((leafRow) => {
          if (leafRow.getCanSelect()) {
            leafRow.toggleSelected(checked);
          }
        });
      } else {
        row.toggleSelected(checked);
      }
    },
    [isGrouped, row]
  );

  return (
    <tr
      data-testid={setRowTestId(row.id)}
      className={cn(
        "border-b border-border transition-colors",
        "hover:bg-muted/30",
        row.getIsSelected() && "bg-muted/50",
        getToneClass(tone),
        hasError && "bg-red-500/5",
        onClick && !isDisabled && "cursor-pointer",
        isGrouped && "bg-muted/20 font-medium"
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick && !isDisabled ? 0 : undefined}
      role={onClick ? "button" : undefined}
      aria-disabled={isDisabled}
      aria-expanded={canExpand ? isExpanded : undefined}
      data-state={row.getIsSelected() ? "selected" : undefined}
      data-depth={depth}
    >
      {/* Expander column for grouped rows */}
      {showExpanderColumn && (
        <td className="w-10 px-2 py-4">
          {canExpand ? (
            <button
              type="button"
              data-testid={setExpanderButtonTestId(row.id)}
              onClick={handleExpandClick}
              className="p-1 hover:bg-muted rounded transition-colors"
              aria-label={isExpanded ? "Collapse row" : "Expand row"}
            >
              {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            </button>
          ) : (
            <span className="w-4 h-4 inline-block" />
          )}
        </td>
      )}
      {selectable && (
        <td className="w-[50px] px-4 py-4" onClick={(e) => e.stopPropagation()}>
          {isLoading && !isGrouped ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <IndeterminateCheckbox
              testId={setRowCheckboxTestId(row.id)}
              checked={isGrouped ? getIsGroupChecked(row, rowSelection) : row.getIsSelected()}
              indeterminate={
                isGrouped ? isGroupDisabled || getIsGroupIndeterminate(row, rowSelection) : isDisabled || row.getIsSomeSelected()
              }
              disabled={isGrouped ? isGroupDisabled : !row.getCanSelect() || isDisabled}
              onChange={handleCheckboxChange}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              aria-label={`Select row ${row.id}`}
            />
          )}
        </td>
      )}
      {isGrouped ? (
        <td colSpan={row.getVisibleCells().length} className={cn("px-4 py-3 text-sm", isGroupDisabled && "text-muted-foreground")}>
          <span className="inline-flex w-full items-center gap-2">
            {row
              .getVisibleCells()
              .filter((cell) => cell.getIsGrouped())
              .map((cell) => (
                <React.Fragment key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</React.Fragment>
              ))}
            {showGroupCount && (
              <span
                className={cn(
                  "inline-flex items-center justify-center rounded-full text-xs font-semibold px-2.5 py-0.5",
                  isGroupDisabled ? "bg-muted-foreground/10 text-muted-foreground" : "bg-primary/10 text-primary"
                )}
              >
                {row.subRows.length}
              </span>
            )}
            {renderGroupSummary && <span className="ml-1">{renderGroupSummary(row.subRows.map((r) => r.original))}</span>}
            {groupedRowActions && helpers && (
              <span className="ml-auto shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                {groupedRowActions(row, helpers)}
              </span>
            )}
          </span>
        </td>
      ) : (
        row.getVisibleCells().map((cell) => (
          <td key={cell.id} className="px-4 py-4 text-sm">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))
      )}
    </tr>
  );
}
